import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_ENCRYPTION_GENERAL } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { GzToggleWebComponent } from '../../../../../../../ui/gzToggle';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { toggleDisabled } from '../../../../../../../utils/htmlElement.utils';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import { EncryptionExclusionItem, EncryptionMode } from '@nirvana/shared/dtos/types/policies/common';
import {
  EncryptionModuleFormValue,
  EncryptionSearchKeywords,
} from '../../../../utils/uiSettings/encryption/encryption.types';
import {
  ENCRYPTION_DEFAULT_VALUE,
  getEncryptionSearchKeywords,
} from '../../../../utils/uiSettings/encryption/encryption.values';
import { GzRadioGroupWebComponent } from '../../../../../../../ui/gzRadio';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzEncryptionExclusionsGridWebComponent } from '../gridComponents/GzEncryptionExclusionsGridWebComponent';
import { GzTextWebComponent } from '../../../../../../../ui/gzText';

@customElement(WEB_COMPONENT_POLICY_PANEL_ENCRYPTION_GENERAL)
export class GzEncryptionGeneralWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  EncryptionModuleFormValue,
  EncryptionSearchKeywords
> {
  /**
   * Path for Encryption module.
   */
  protected path = {
    moduleId: ModuleId.ENCRYPTION,
    sectionId: SectionId.GENERAL,
  };

  /**
   * Searchable keywords for Encryption section.
   */
  protected searchableKeywords = getEncryptionSearchKeywords();

  // encryption toggle and content
  @query('#encryption-toggle')
  private encryptionToggle: GzFormFieldWebComponent<GzToggleWebComponent, boolean>;
  @query('#encryption-info-text')
  private encryptionInfoText: GzTextWebComponent;
  @query('#encryption-content')
  private encryptionContent: HTMLElement;

  // encryption mode section
  @query('#encryption-mode-form-field')
  private encryptionModeFormField: GzFormFieldWebComponent<GzRadioGroupWebComponent, string | number>;
  @query('#tpm-form-field')
  private tpmFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // encryption exclusions section
  @query('#exclusions-checkbox-form-field')
  private exclusionsCheckboxFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;
  @query('#exclusions-content')
  private exclusionsContent: HTMLElement;
  @query('#exclusions-grid-form-field')
  private exclusionsGridFormField: GzFormFieldWebComponent<
    GzEncryptionExclusionsGridWebComponent,
    EncryptionExclusionItem[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-form-field id="encryption-toggle" status-field>
      <gz-toggle
        class="content"
        searchable-label="${this.searchableKeywords[EncryptionSearchKeywords.EncryptionManagement].label}"
        gz-form-input
        @toggleChange="${this.onToggleChange}"
      >
        <span>${this.searchableKeywords[EncryptionSearchKeywords.EncryptionManagement].translatedLabel}</span>
      </gz-toggle>
    </gz-form-field>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text id="encryption-info-text" fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Enable this module to start managing endpoint encryption from Control Center. Disabling it will leave volumes in their current state and will allow users to manage encryption locally.'
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div id="encryption-content">
        <div>
          <div class="section">
            <gz-form-field id="encryption-mode-form-field">
              <gz-radio-group
                gz-form-input
                class="d-flex flex-column"
                @radioSelectionChanged=${e =>
                  toggleDisabled(this.tpmFormField, e.detail.selectedValue === EncryptionMode.DECRYPT)}
              >
                <div class="mb-2">
                  <gz-radio
                    searchable-label="${this.searchableKeywords[EncryptionSearchKeywords.Decrypt].label}"
                    label="${this.searchableKeywords[EncryptionSearchKeywords.Decrypt].translatedLabel}"
                    .value=${EncryptionMode.DECRYPT}
                  ></gz-radio>
                  <gz-text fontSize="12" fontWeight="400" class="helper-text"
                    >${translateFn('Select this option to decrypt volumes.')}</gz-text
                  >
                </div>

                <div>
                  <gz-radio
                    searchable-label="${this.searchableKeywords[EncryptionSearchKeywords.Encrypt].label}"
                    label="${this.searchableKeywords[EncryptionSearchKeywords.Encrypt].translatedLabel}"
                    .value=${EncryptionMode.ENCRYPT}
                  ></gz-radio>

                  <gz-text fontSize="12" fontWeight="400" class="helper-text"
                    >${translateFn(
                      'Select this option to encrypt volumes. Users will be prompted to enter a password that will be required for pre-boot authentication.'
                    )}</gz-text
                  >
                  <gz-form-field id="tpm-form-field" class="mt-2">
                    <gz-checkbox
                      searchable-label="${this.searchableKeywords[EncryptionSearchKeywords.TrustedPlatformModule]
                        .label} ${this.searchableKeywords[EncryptionSearchKeywords.Tpm].label}"
                      gz-form-input
                      label="${translateFn(
                        'If Trusted Platform Module (TPM) is active, do not ask for pre-boot password.'
                      )}"
                    ></gz-checkbox>
                  </gz-form-field>
                </div>
              </gz-radio-group>
            </gz-form-field>
          </div>
        </div>

        <div class="section width-100">
          <gz-form-field id="exclusions-checkbox-form-field">
            <gz-checkbox
              searchable-label="${this.searchableKeywords[EncryptionSearchKeywords.Exclusions].label}"
              gz-form-input
              label="${this.searchableKeywords[EncryptionSearchKeywords.Exclusions].translatedLabel}"
              class="checkbox-subtitle"
              @change="${e => toggleDisabled(this.exclusionsContent, !e.detail.checked)}"
            ></gz-checkbox>
          </gz-form-field>

          <div id="exclusions-content">
            <gz-text fontSize="12" fontWeight="400" class="helper-text"
              >${translateFn('The partition with the operating system cannot be excluded from encryption.')}</gz-text
            >

            <gz-form-field id="exclusions-grid-form-field">
              <gz-policy-panel-encryption-exclusions-grid gz-form-input></gz-policy-panel-encryption-exclusions-grid>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.encryption
   */
  protected getValueFromManager(): EncryptionModuleFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.ENCRYPTION]: ENCRYPTION_DEFAULT_VALUE,
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.ENCRYPTION]: PolicyPanelManager.getInstance().getPolicy('uiSettings.encryption'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        encryption: {
          enabled: this.encryptionToggle,
          mode: this.encryptionModeFormField,
          encryptPolicy: {
            tpm: {
              shouldAskForPassword: this.tpmFormField,
            },
          },
          exclusions: {
            enabled: this.exclusionsCheckboxFormField,
            items: this.exclusionsGridFormField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        encryption: {
          enabled: this.valueT.uiSettings.encryption.enabled,
          mode: this.valueT.uiSettings.encryption.mode,
          encryptPolicy: this.valueT.uiSettings.encryption.encryptPolicy,
          exclusions: this.valueT.uiSettings.encryption.exclusions,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);

    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'encryption'], {});
    }

    return value;
  }

  /**
   * Enable/Disable module.
   * @param event
   */
  onToggleChange(event: CustomEvent) {
    toggleDisabled(this.encryptionInfoText, !event.detail.checked);
    toggleDisabled(this.encryptionContent, !event.detail.checked);
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.exclusionsGridFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_ENCRYPTION_GENERAL: GzEncryptionGeneralWebComponent;
  }
}
